// utils
import {
  getSignupAccountPhoneAlreadyRegisteredPageTypeAndSchema,
  getSignupPageTypeAndSchema,
  getSignupAccountEmailAlreadyRegisteredPageTypeAndSchema,
  getSignupSuccessPageTypeAndSchema,
  getSignupSetPasswordPageTypeAndSchema,
} from '@shopee-user/business-customization-logic';

// constants
import { SignupTrackingPoint } from '@shopee-user/tracking-constants';
import { TrackingOperation } from '@shopee-user/tracking';

// types
import { TrackingInfo } from '@shopee-user/tracking';
import {
  TrackingDataFieldTypeSignupMethod,
  TrackingDataSignupProps,
  TrackingDataFieldSignup,
  TrackingDataLoginProps,
  TrackingDataFieldLogin,
} from './types';
import { converOtpTypeToPhoneVerificationChannel } from '@tob/shared/whatsapp-auth-link/tracking';
import { OtpType } from '@shopee-user/otp-hook-tob';
import { convertBiometricTypeToTrackingData } from '@tob/shared/biometrics/tracking';

export function generateTrackingSignupView(): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.View,
    ...getSignupPageTypeAndSchema(SignupTrackingPoint.SignupPageView),
  };
}

export function generateTrackingHelpButtonClick(): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'help_button',
    ...getSignupPageTypeAndSchema(SignupTrackingPoint.SignupHelpButtonClick),
  };
}

export function generateTrackingNextButtonClick({
  method,
}: TrackingDataSignupProps): TrackingInfo<TrackingDataFieldSignup> {
  return {
    operation: TrackingOperation.Click,
    data: { method },
    target_type: 'next_button',
    ...getSignupPageTypeAndSchema(SignupTrackingPoint.SignupNextButtonClick),
  };
}

export function generateTrackingSubmitFormButtonClick(): TrackingInfo<
  undefined
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'submit_form_button',
    ...getSignupPageTypeAndSchema(
      SignupTrackingPoint.EnterpriseAccountSubmitFormButtonClick
    ),
  };
}

export function generateTrackingSignupAlreadyRegisteredView({
  method,
}: TrackingDataSignupProps): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.View,
    ...(method === TrackingDataFieldTypeSignupMethod.Phone
      ? getSignupAccountPhoneAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.PhoneAlreadyRegisteredPageView
        )
      : getSignupAccountEmailAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.EmailAlreadyRegisteredPageView
        )),
  };
}

export function generateTrackingAlreadyRegisteredLoginButtonClick({
  method,
}: TrackingDataSignupProps): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'log_in_button',
    ...(method === TrackingDataFieldTypeSignupMethod.Phone
      ? getSignupAccountPhoneAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.PhoneAlreadyRegisteredLoginButtonClick
        )
      : getSignupAccountEmailAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.EmailAlreadyRegisteredLoginButtonClick
        )),
  };
}

export function generateTrackingAlreadyRegisteredHelpButtonClick({
  method,
}: TrackingDataSignupProps): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'help_button',
    ...(method === TrackingDataFieldTypeSignupMethod.Phone
      ? getSignupAccountPhoneAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.PhoneAlreadyRegisteredHelpButtonClick
        )
      : getSignupAccountEmailAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.EmailAlreadyRegisteredHelpButtonClick
        )),
  };
}

export function generateTrackingAlreadyRegisteredContactUsButtonClick({
  method,
}: TrackingDataSignupProps): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'contact_us_button',
    ...(method === TrackingDataFieldTypeSignupMethod.Phone
      ? getSignupAccountPhoneAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.PhoneAlreadyRegisteredContactUsButtonClick
        )
      : getSignupAccountEmailAlreadyRegisteredPageTypeAndSchema(
          SignupTrackingPoint.EmailAlreadyRegisteredContactUsButtonClick
        )),
  };
}

export function generateTrackingSignupSuccessAction({
  method,
  accountType,
  otpType,
}: TrackingDataSignupProps): TrackingInfo<TrackingDataFieldSignup> {
  return {
    operation: TrackingOperation.SignupSuccessAction,
    data: {
      method,
      account_type: accountType,
      phone_verification_channel: converOtpTypeToPhoneVerificationChannel(
        otpType as OtpType
      ),
    },
    ...getSignupPageTypeAndSchema(SignupTrackingPoint.SignupSuccessAction),
  };
}

export function generateTrackingSignupSetPasswordView(): TrackingInfo<{
  scenario: string;
}> {
  return {
    operation: TrackingOperation.View,
    data: { scenario: 'sign_up' },
    ...getSignupSetPasswordPageTypeAndSchema(
      SignupTrackingPoint.SignupSetPasswordPageView
    ),
  };
}

export function generateTrackingSignupSetPasswordSignupButtonClick(): TrackingInfo<{
  scenario: string;
}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'sign_up_button',
    data: { scenario: 'sign_up' },
    ...getSignupSetPasswordPageTypeAndSchema(
      SignupTrackingPoint.SignupSetPasswordSignupButtonClick
    ),
  };
}

export function generateTrackingSignupSetPasswordBackButtonClick(): TrackingInfo<{
  scenario: string;
}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    data: { scenario: 'sign_up' },
    ...getSignupSetPasswordPageTypeAndSchema(
      SignupTrackingPoint.SignupSetPasswordBackButtonClick
    ),
  };
}

export function generateTrackingSignupSuccessView(): TrackingInfo<undefined> {
  return {
    operation: TrackingOperation.View,
    ...getSignupSuccessPageTypeAndSchema(
      SignupTrackingPoint.SignupSuccessPageView
    ),
  };
}

export function generateTrackingSignupSuccessNextButtonClick(): TrackingInfo<
  undefined
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'next_button',
    ...getSignupSuccessPageTypeAndSchema(
      SignupTrackingPoint.SignupSuccessPageNextButtonClick
    ),
  };
}

export function generateTrackingSignupSuccessBackButtonClick(): TrackingInfo<
  undefined
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    ...getSignupSuccessPageTypeAndSchema(
      SignupTrackingPoint.SignupSuccessPageBackButtonClick
    ),
  };
}

export function generateTrackingLoginSuccessAction({
  loginMethod,
  accountType,
  biometricType,
  otpType,
}: TrackingDataLoginProps): TrackingInfo<TrackingDataFieldLogin> {
  return {
    operation: TrackingOperation.LoginSuccessAction,
    data: {
      login_method: loginMethod,
      account_type: accountType,
      biometrics_type: biometricType
        ? convertBiometricTypeToTrackingData(biometricType)
        : undefined,
      phone_verification_channel: converOtpTypeToPhoneVerificationChannel(
        otpType as OtpType
      ),
    },
    ...getSignupPageTypeAndSchema(SignupTrackingPoint.LoginSuccessAction),
  };
}
